import { UniqueIdentifier } from '@dnd-kit/core';
import { isEmpty, isNil } from 'lodash';
import React, { useCallback } from 'react';
import { FlatList, ListRenderItemInfo, RefreshControl, StyleSheet } from 'react-native';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import { Entity } from '@shared/data/entity/Entity';
import { useUpgradeModal } from '@shared/scenes/billing/upgradeModal/provider';
import { MBCardEmptyState } from '@shared/scenes/cards/components/emptyState';
import { PRICING_LIMIT_HISTORY_DAYS, grid } from '@shared/styles';
import {
  usePaywallExperiment,
  useTrackPaywallExperimentClick,
} from '@shared/util/featureFlags/launchDarkly/hooks/usePaywallExperiment';
import { getTestId } from '@shared/util/getTestId';
import { i18n } from '@shared/util/i18n';
import { CollectionViewAnalytics } from '../../collectionView/CollectionViewAnalytics';
import { OpenEntityHandler } from '../../collectionView/hooks/useCollectionViewData';
import { CardListFooter } from './CardListFooter';
import { CardListItem } from './CardListItem';
import ListViewCardStyles from './ListViewCardStyles.module.css';

interface Props {
  workflowTemplate: MBAPI.WorkflowTemplate;
  viewTemplate: MBAPI.ViewTemplate;
  canUpdateCard: boolean;
  entities: Entity[];
  openEntity: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  showMore(): void;
  refetch?(): void;
  hasMore: boolean;
  isMoreLoading: boolean;
  isCardsLoading: boolean;
  isGroup: boolean;
  hasOverflowMenu?: boolean;
  isList?: boolean;
  isBoard?: boolean;
  isNarrowBoard?: boolean;
  groupId?: UniqueIdentifier;
  testID?: string;
}

export function CardList({
  workflowTemplate,
  viewTemplate,
  canUpdateCard,
  entities,
  openEntity,
  getIsCardBeforePaidCutoff,
  hasMore,
  showMore,
  isCardsLoading,
  isMoreLoading,
  isGroup,
  refetch,
  hasOverflowMenu = true,
  isList,
  isBoard,
  isNarrowBoard,
  groupId,
  testID,
}: Props) {
  const { show: showUpgradeModal } = useUpgradeModal();
  const reportRedactedClick = useTrackPaywallExperimentClick('redacted');
  const { isRedactedGroup } = usePaywallExperiment();
  const analytics = new CollectionViewAnalytics(workflowTemplate.id, viewTemplate.id);

  const showModal = useCallback(() => {
    reportRedactedClick();
    showUpgradeModal({
      title: i18n('card.upgradeModal.title', { count: PRICING_LIMIT_HISTORY_DAYS }),
      description: i18n('card.upgradeModal.description'),
    });
  }, [showUpgradeModal, reportRedactedClick]);

  const renderItem = (item: ListRenderItemInfo<Entity>) => {
    const isRedacted = isRedactedGroup && getIsCardBeforePaidCutoff(item.item);

    return (
      <CardListItem
        key={item.item.id}
        viewTemplate={viewTemplate}
        workflowTemplate={workflowTemplate}
        openEntity={isRedacted ? showModal : openEntity}
        getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
        entity={item.item}
        canUpdateCard={canUpdateCard}
        cardStyles={isList ? ListViewCardStyles : undefined}
        isRedacted={isRedacted}
        analytics={analytics}
        hasOverflowMenu={hasOverflowMenu}
        testID={getTestId('card-list-item', item.index)}
        groupId={groupId}
      />
    );
  };

  const isLoading = isCardsLoading || isMoreLoading;
  const isEmptyState = isEmpty(entities) && !isLoading;
  const emptyTitle = isGroup ? i18n('card.table.emptyGroup.title') : i18n('emptyCardList.title');
  const emptyMessage = isGroup
    ? i18n('card.table.emptyGroup.description')
    : i18n('emptyCardList.description');

  return (
    <FlatList
      testID={testID}
      data={entities}
      refreshControl={
        !isNil(refetch) ? (
          <RefreshControl refreshing={isCardsLoading} onRefresh={refetch} />
        ) : undefined
      }
      renderItem={renderItem}
      contentContainerStyle={[
        isList && CardListContainerStyles.list,
        isBoard && CardListContainerStyles.board,
        isNarrowBoard && CardListContainerStyles.narrowUngroupedBoard,
      ]}
      ListFooterComponent={
        <>
          {isEmptyState && <MBCardEmptyState title={emptyTitle} message={emptyMessage} />}
          <CardListFooter {...{ showMore, hasMore, isLoading, isMoreLoading }} />
        </>
      }
    />
  );
}

const CardListContainerStyles = StyleSheet.create({
  list: {
    paddingHorizontal: grid(1),
    paddingBottom: grid(10),
    flexGrow: 1,
  },
  board: {
    width: grid(40),
    paddingBottom: grid(10),
    flexGrow: 1,
  },
  narrowUngroupedBoard: {
    width: '100%',
  },
});
